// Console URLs (ADR-107, ui-spec §2.8): `/<org>/<project>/<resource>/<name>[/<tab>]` for project kinds, org pages
// under `/<org>/{settings,team,billing,integrations,infrastructure}`, and generated aliases for every plural without a
// page of its own. linkTo() is total over the 46 kinds: every collection and every object resolves.
//
// This is the interim builder with the same signature tools/linkgen (F3) generates into links.ts; the barrel
// re-exports whichever is present.
import { KINDS, kindInfo } from "./kinds";

export interface LinkTarget {
  kind: string;
  /** Omitted for the collection link. */
  name?: string;
  /** The parent for kinds shown inside another object's page (Attempt, Process, FunctionCall, Event…). */
  owner?: { kind: string; name: string };
}

export interface LinkScope {
  org: string;
  /** Required for project kinds; the catalog project `nodus` works like any other. */
  project?: string;
  tab?: string;
  /** Extra query parameters (`?focus=`, `?tab=`…), merged with the alias's own. */
  query?: Record<string, string | undefined>;
}

/** Kinds with their own list and detail pages, and their default tab (a Job opens on its log, a TrainingJob on its
 * curves and results). */
const PAGES: Record<string, { tab?: string; collection?: string }> = {
  Job: { tab: "logs", collection: "runs?kind=Job" },
  Pipeline: { tab: "overview", collection: "runs?kind=Pipeline" },
  Sweep: { tab: "overview", collection: "runs?kind=Sweep" },
  TrainingJob: { tab: "overview", collection: "environments?tab=runs" },
  Workspace: { tab: "overview" },
  ReservedHost: { tab: "overview" },
  Sandbox: { tab: "overview" },
  Agent: { tab: "overview" },
  AgentRun: { tab: "overview", collection: "agents?tab=runs" },
  AgentGroup: { tab: "overview", collection: "agents?tab=groups" },
  InferenceEndpoint: { tab: "overview", collection: "inference?section=endpoints" },
  Environment: {},
  TrainingRuntime: {},
  Function: { tab: "overview" },
  App: { collection: "functions?tab=apps" },
  Volume: { tab: "overview" },
  Image: { tab: "overview" },
  Secret: {},
  Connection: {},
  Pool: { tab: "overview" },
  ServiceAccount: {},
};

/** Org-scoped kinds that live on a Settings, Team, Billing or Integrations page, with the focus prefix. */
const ORG_PAGES: Record<string, { page: string; focus?: string; object?: (name: string) => string }> = {
  Organization: { page: "settings/general" },
  Project: { page: "team/projects", focus: "proj" },
  Member: { page: "team/members", focus: "member" },
  Invite: { page: "team/invites", focus: "invite" },
  APIKey: { page: "settings/apikeys", focus: "key" },
  OAuthGrant: { page: "integrations/mcp", focus: "oauth" },
  Quota: { page: "settings/quotas", focus: "quota" },
  WebhookEndpoint: { page: "settings/webhooks", object: (n) => `settings/webhooks/${encodeURIComponent(n)}` },
  AuditEvent: { page: "settings/audit", focus: "audit" },
  BillingAccount: { page: "billing/overview" },
  TopUp: { page: "billing/receipts", focus: "topup" },
  Budget: { page: "billing/budgets", object: (n) => `billing/budgets/${encodeURIComponent(n)}` },
  CreditGrant: { page: "billing/credits", focus: "grant" },
  Transaction: { page: "billing/transactions", focus: "txn" },
  UsageRecord: { page: "billing/usage", focus: "usage" },
  Node: { page: "infrastructure/nodes", object: (n) => `infrastructure/nodes/${encodeURIComponent(n)}` },
  CloudAccount: { page: "infrastructure/cloudaccounts", object: (n) => `infrastructure/cloudaccounts/${encodeURIComponent(n)}` },
};

function withQuery(path: string, query: Record<string, string | undefined> = {}): string {
  const [base, existing] = path.split("?", 2) as [string, string | undefined];
  const params = new URLSearchParams(existing);
  for (const [key, v] of Object.entries(query)) if (v !== undefined) params.set(key, v);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function short(kind: string): string {
  return kindInfo(kind).shortNames[0]!;
}

export function linkTo(target: LinkTarget, scope: LinkScope): string {
  const info = kindInfo(target.kind);
  const org = `/${encodeURIComponent(scope.org)}`;
  const project = scope.project ?? "default";
  const proj = `${org}/${encodeURIComponent(project)}`;
  const name = target.name !== undefined ? encodeURIComponent(target.name) : undefined;
  const q = scope.query;

  if (info.kind === "MarketplaceEntry" || info.kind === "MarketplaceContract") return withQuery(`/marketplace${org}`, { view: info.kind === "MarketplaceContract" ? "Contracts" : "Requests", ...q });
  if (info.kind === "SSHKey") return withQuery("/account/sshkeys", name ? { focus: `sshkey/${target.name}`, ...q } : q);
  if (info.kind === "Offering") return withQuery(`${proj}/runs`, { tab: "gpus", ...(name ? { focus: `offering/${target.name}` } : {}), ...q });
  if (info.kind === "Model") {
    return withQuery(name ? `${org}/nodus/models/${name}` : `${org}/nodus/models`, q);
  }

  const page = PAGES[info.kind];
  if (page) {
    if (!name) return withQuery(`${proj}/${page.collection ?? info.resource}`, q);
    const tab = scope.tab ?? page.tab;
    const prefix = info.kind === "App" ? "apps" : info.resource;
    return withQuery(`${proj}/${prefix}/${name}${tab ? `/${encodeURIComponent(tab)}` : ""}`, q);
  }

  const orgPage = ORG_PAGES[info.kind];
  if (orgPage) {
    if (name && orgPage.object) return withQuery(`${org}/${orgPage.object(target.name!)}`, q);
    const focus = name && orgPage.focus ? { focus: `${orgPage.focus}/${target.name}` } : {};
    return withQuery(`${org}/${orgPage.page}`, { ...focus, ...q });
  }

  // Kinds shown inside their owner's page.
  const owner = target.owner;
  const focus = name ? { focus: `${short(info.kind)}/${target.name}` } : {};
  switch (info.kind) {
    case "Attempt": {
      if (!name) return withQuery(`${proj}/runs`, q);
      if (owner?.kind === "Workspace") return withQuery(`${proj}/workspaces/${encodeURIComponent(owner.name)}/sessions`, { ...focus, ...q });
      if (owner) return withQuery(linkTo(owner, { ...scope, tab: "attempts", query: undefined }), { ...focus, ...q });
      return withQuery(`${proj}/runs`, { ...focus, ...q });
    }
    case "Process": {
      if (owner && name) return withQuery(linkTo(owner, { ...scope, tab: "processes", query: undefined }), { process: target.name, ...q });
      return withQuery(`${proj}/sandboxes`, { ...(name ? { focus: `proc/${target.name}` } : {}), ...q });
    }
    case "FunctionCall": {
      if (owner && name) return withQuery(linkTo(owner, { ...scope, tab: "calls", query: undefined }), { call: target.name, ...q });
      return withQuery(`${proj}/functions`, { ...focus, ...q });
    }
    case "PoolAction": {
      if (owner && name) return withQuery(linkTo(owner, { ...scope, tab: "approvals", query: undefined }), { ...focus, ...q });
      return withQuery(`${proj}/pools`, { tab: "approvals", ...focus, ...q });
    }
    case "EnrollmentToken": {
      if (owner && name) return withQuery(linkTo(owner, { ...scope, tab: "nodes", query: undefined }), { ...focus, ...q });
      return withQuery(`${proj}/pools`, { tab: "nodes", ...focus, ...q });
    }
    case "Event": {
      if (owner && name) return withQuery(linkTo(owner, { ...scope, tab: "events", query: undefined }), { ...focus, ...q });
      return withQuery(`${org}/settings/audit`, { ...focus, ...q });
    }
    default:
      throw new Error(`linkTo has no route for ${info.kind}`);
  }
}

/** Every kind the builder serves; the totality test iterates this. */
export const LINKABLE_KINDS = KINDS.map((k) => k.kind);
